import paths from 'src/routes/paths'
import { strings, APPROVAL_STATUS, APPROVAL_TYPE, enquiryStatus } from '@constants'
import { displayAmount } from '@utils'

const ENQUIRY_STATUS_COLOR = {
	[enquiryStatus.created]: 'neutral',
	[enquiryStatus.unverified]: 'warning',
	[enquiryStatus.active]: 'info',
	[enquiryStatus.orderPlaced]: 'success',
	[enquiryStatus.expired]: 'neutral',
	[enquiryStatus.cancelled]: 'error',
	[enquiryStatus.deleted]: 'error',
}

export const getEnquiryStatusColor = (status) => ENQUIRY_STATUS_COLOR[status] || 'neutral'

const REASON_TITLE = {
	[APPROVAL_TYPE.CREDIT_LIMIT]: () => strings('credit_limit'),
	[APPROVAL_TYPE.MARGIN]: () => strings('contribution_margin'),
	[APPROVAL_TYPE.OVERDUE]: () => strings('overdue_check'),
	[APPROVAL_TYPE.PAYMENT_TERM]: () => strings('payment_terms'),
	[APPROVAL_TYPE.INTEREST_PAYMENT]: () => strings('interest_payment'),
	[APPROVAL_TYPE.SUPPLIER_PO]: () => strings('supplier_po'),
}

export const getStatusFromPathname = (pathname) => {
	switch (pathname) {
		case paths.admin_approval_metrics_approved:
			return APPROVAL_STATUS.APPROVED
		case paths.admin_approval_metrics_declined:
			return APPROVAL_STATUS.REJECTED
		case paths.admin_approval_metrics_pending:
			return APPROVAL_STATUS.PENDING
		case paths.admin_approval_metrics_all_actions:
		default:
			return null
	}
}

export const getStatusPill = (status) => {
	switch (status) {
		case APPROVAL_STATUS.APPROVED:
			return { label: strings('approved'), color: 'success' }
		case APPROVAL_STATUS.AUTO_APPROVED:
			return { label: strings('auto_approved'), color: 'success' }
		case APPROVAL_STATUS.REJECTED:
			return { label: strings('rejected'), color: 'error' }
		case APPROVAL_STATUS.PENDING:
		default:
			return { label: strings('pending'), color: 'warning' }
	}
}

const titleCase = (value) => (value ? value.charAt(0) + value.slice(1).toLowerCase() : value)

const formatPaymentTerms = (terms) =>
	terms?.length
		? terms?.map((term) => `${term.percentage || 0}% ${titleCase(term.payment_term)} (${term.tenure_in_days || 0} ${strings('days')})`).join(', ')
		: '--'

const buildFields = (approval, hideSupplierName) => {
	const metrics = approval?.metrics || {}

	switch (approval?.approval_type) {
		case APPROVAL_TYPE.CREDIT_LIMIT: {
			const shortfall = Math.max(0, (metrics.required_credit_limit || 0) - (metrics.available_credit_limit || 0))
			return [
				{ kind: 'chip', color: 'info', label: strings('required'), value: displayAmount(metrics.required_credit_limit || 0) },
				{ kind: 'chip', color: 'neutral', square: true, label: strings('shortfall'), value: displayAmount(shortfall), valueColor: 'error' },
				{ kind: 'kv', label: strings('sanctioned'), value: displayAmount(metrics.total_credit_limit || 0) },
				{ kind: 'kv', label: strings('available'), value: displayAmount(metrics.available_credit_limit || 0) },
				{ kind: 'kv', label: strings('all_required_limit'), value: displayAmount(metrics.total_active_po_amount || 0), link: true },
				// {
				// 	kind: 'chip',
				// 	color: 'info',
				// 	variant: 'outline',
				// 	label: strings('all_open_enquiries'),
				// 	value: displayAmount(metrics.total_active_po_amount || 0),
				// 	link: true,
				// },
			]
		}
		case APPROVAL_TYPE.MARGIN: {
			const gap = (metrics.cm_margin || 0) - (metrics.required_margin || 0)
			return [
				{
					kind: 'chip',
					color: 'neutral',
					square: true,
					fullRow: true,
					label: strings('gap'),
					value: displayAmount(Math.abs(gap)),
					valueColor: gap < 0 ? 'error' : 'success',
				},
				{ kind: 'kv', label: strings('required'), value: displayAmount(metrics.required_margin || 0) },
				{ kind: 'kv', label: strings('reported'), value: displayAmount(metrics.cm_margin || 0) },
				{ kind: 'kv', label: strings('marketing_expense'), value: displayAmount(metrics.marketing_expense || 0) },
			]
		}
		case APPROVAL_TYPE.OVERDUE: {
			return metrics.over_due_amount
				? [
						{
							kind: 'kv',
							label: strings('overdue_amount'),
							value: displayAmount(metrics.over_due_amount),
							link: true,
						},
				  ]
				: [{ kind: 'chip', color: 'success', label: strings('no_dues') }]
		}
		case APPROVAL_TYPE.PAYMENT_TERM: {
			return [
				{ kind: 'kv', label: strings('approved_terms'), value: formatPaymentTerms(metrics.payment_terms_org) },
				{ kind: 'kv', label: strings('requested_terms'), value: formatPaymentTerms(metrics.payment_terms_enquiry) },
			]
		}
		case APPROVAL_TYPE.INTEREST_PAYMENT: {
			return [
				{ kind: 'kv', label: strings('interest_received'), value: metrics.interest_received ? strings('yes') : strings('no') },
				{ kind: 'kv', label: strings('average_delay'), value: `${metrics.avg_delay || 0} ${strings('days')}` },
			]
		}
		case APPROVAL_TYPE.SUPPLIER_PO: {
			return [
				// supplier identity is not disclosed to sales admins
				...(hideSupplierName
					? []
					: [{ kind: 'chip', color: 'info', fullRow: true, label: strings('supplier'), value: metrics.supplier_name || '--' }]),
				{ kind: 'kv', label: strings('po_amount'), value: displayAmount(metrics.po_amount || 0), link: true },
			]
		}
		default:
			return []
	}
}

export const getEnquiryPoAmount = (row) => {
	const creditApproval = (row?.approvals || []).find((approval) => approval?.approval_type === APPROVAL_TYPE.CREDIT_LIMIT)
	return creditApproval?.metrics?.po_amount || 0
}

export const isApprovalMember = (approval, userId) => !!userId && (approval?.members || []).some((member) => member?.user_details?.user_id === userId)

const getApproverName = (approval) => {
	const approver = (approval?.members || []).find((member) => member?.approved)
	return approver?.user_details?.full_name?.trim() || null
}

// fulfillment members can come back with only user_id/mobile_number, so fall back to the
// fuller user objects elsewhere on the approval (requested_by, status_history) for the name
const getMemberName = (approval, userDetails) => {
	if (userDetails?.full_name?.trim()) return userDetails.full_name.trim()
	const knownUsers = [approval?.requested_by, ...(approval?.status_history || []).map((entry) => entry?.changed_by)]
	const match = knownUsers.find((user) => user?.user_id && user.user_id === userDetails?.user_id && user?.full_name?.trim())
	return match?.full_name?.trim() || userDetails?.email_id || userDetails?.mobile_number || null
}

const getMemberNamesByStatus = (approval, memberStatus) =>
	(approval?.members || [])
		.filter((member) => member?.member_status === memberStatus)
		.map((member) => getMemberName(approval, member?.user_details))
		.filter(Boolean)

// member/status bits shared by every approval card (enquiry reasons and fulfillment plans)
export const getApprovalActionState = (approval, userId) => {
	const isMember = isApprovalMember(approval, userId)
	const isPending = approval?.status === APPROVAL_STATUS.PENDING
	return {
		key: approval?.credit_approval_id,
		status: getStatusPill(approval?.status),
		approvedBy: approval?.status === APPROVAL_STATUS.APPROVED ? getApproverName(approval) : null,
		approvers: getMemberNamesByStatus(approval, APPROVAL_STATUS.APPROVED),
		rejections: getMemberNamesByStatus(approval, APPROVAL_STATUS.REJECTED),
		pending: getMemberNamesByStatus(approval, APPROVAL_STATUS.PENDING),
		isMember,
		isPending,
		showActions: isPending && isMember,
	}
}

export const getApprovalReasons = (row, userId, hideSupplierName) =>
	(row?.approvals || [])?.map((approval) => ({
		...getApprovalActionState(approval, userId),
		approvalType: approval?.approval_type,
		supplierQuoteId: approval?.supplier_quote_id,
		metrics: approval?.metrics || {},
		title: (REASON_TITLE[approval?.approval_type] || (() => approval?.approval_type))(),
		fields: buildFields(approval, hideSupplierName),
	}))
